import { useState, useEffect } from 'react';
import { useAppSelector } from '../store/hooks';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  EventNote as EventNoteIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as TrophyIcon,
  Assessment as AssessmentIcon,
  FitnessCenter as TrainingIcon,
  SportsScore as MatchIcon,
} from '@mui/icons-material';
import { sessionApi, Session } from '../api/session.api';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import { SessionFormDialog } from '../components/sessions/SessionFormDialog';

export const SessionsPage = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const isPlayer = user?.role === 'PLAYER';

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError('');
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await sessionApi.getMySessions(params);
      setSessions(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPlayer) {
      fetchSessions();
    }
  }, [statusFilter, isPlayer]);

  const getSessionTypeIcon = (type: string) => {
    switch (type) {
      case 'training':
        return <TrainingIcon />;
      case 'match':
        return <MatchIcon />;
      case 'assessment':
        return <AssessmentIcon />;
      case 'event':
        return <TrophyIcon />;
      default:
        return <EventNoteIcon />;
    }
  };

  const getSessionTypeColor = (type: string) => {
    switch (type) {
      case 'training':
        return 'primary';
      case 'match':
        return 'error';
      case 'assessment':
        return 'warning';
      case 'event':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Chip label="Scheduled" color="info" size="small" />;
      case 'in_progress':
        return <Chip label="In Progress" color="warning" size="small" />;
      case 'completed':
        return <Chip label="Completed" color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'cancelled':
        return <Chip label="Cancelled" color="error" size="small" icon={<CancelIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return `Today, ${format(date, 'h:mm a')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow, ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'EEE, MMM d, h:mm a');
    }
  };

  const groupSessionsByStatus = () => {
    const upcoming = sessions.filter(
      (s) => s.status === 'scheduled' && !isPast(new Date(s.startTime))
    );
    const past = sessions.filter(
      (s) => s.status === 'completed' || isPast(new Date(s.startTime))
    );
    const cancelled = sessions.filter((s) => s.status === 'cancelled');
    return { upcoming, past, cancelled };
  };

  const { upcoming, past, cancelled } = groupSessionsByStatus();

  if (!isPlayer) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">
          Session management for coaches and admins will be available through the squad detail page.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          My Squads
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Sessions</MenuItem>
          <MenuItem value="scheduled">Scheduled</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
      </Box>

      {sessions.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <EventNoteIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No sessions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You don't have any sessions scheduled yet.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          {upcoming.length > 0 && (
            <>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Upcoming Sessions ({upcoming.length})
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {upcoming.map((session) => (
                  <Grid item xs={12} key={session.id}>
                    <Card
                      sx={{
                        borderLeft: `4px solid`,
                        borderLeftColor: `${getSessionTypeColor(session.sessionType)}.main`,
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              {getSessionTypeIcon(session.sessionType)}
                              <Typography variant="h6" fontWeight={600}>
                                {session.title}
                              </Typography>
                              <Chip
                                label={session.sessionType.toUpperCase()}
                                size="small"
                                color={getSessionTypeColor(session.sessionType)}
                              />
                              {getStatusChip(session.status)}
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {session.squad?.name}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon fontSize="small" />
                                <Typography variant="body2">
                                  {formatSessionDate(session.startTime)} - {format(new Date(session.endTime), 'h:mm a')}
                                </Typography>
                              </Box>
                              {session.location && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationOnIcon fontSize="small" />
                                  <Typography variant="body2">{session.location}</Typography>
                                </Box>
                              )}
                            </Box>

                            {session.description && (
                              <Typography variant="body2" color="text.secondary">
                                {session.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {past.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Past Sessions ({past.length})
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {past.map((session) => (
                  <Grid item xs={12} key={session.id}>
                    <Card sx={{ opacity: 0.8 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6">{session.title}</Typography>
                              <Chip label={session.sessionType.toUpperCase()} size="small" variant="outlined" />
                              {getStatusChip(session.status)}
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {session.squad?.name}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              {format(new Date(session.startTime), 'EEE, MMM d, yyyy • h:mm a')}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {cancelled.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Cancelled Sessions ({cancelled.length})
              </Typography>
              <Grid container spacing={2}>
                {cancelled.map((session) => (
                  <Grid item xs={12} key={session.id}>
                    <Card sx={{ opacity: 0.7, bgcolor: 'error.light' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6">{session.title}</Typography>
                              {getStatusChip(session.status)}
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {session.squad?.name}
                            </Typography>

                            {session.cancelReason && (
                              <Typography variant="body2" color="error.dark">
                                Reason: {session.cancelReason}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}

      {dialogOpen && (
        <SessionFormDialog
          open={dialogOpen}
          onClose={(refreshNeeded) => {
            setDialogOpen(false);
            if (refreshNeeded) fetchSessions();
          }}
        />
      )}
    </Container>
  );
};
